import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  IonPage,
  IonContent,
  IonItem,
  IonLabel,
  IonText,
  IonIcon,
  IonCheckbox,
  useIonViewWillLeave,
} from '@ionic/react';
import { callOutline } from 'ionicons/icons';
import { FooterDock, FooterDockRow, SmartCaseButton } from '@/modules/footer';
import { useUserContext } from '@/modules/user/useUserContext';
import { useProfileWizardContext } from '@/contexts/ProfileWizardContext';
import { ProfileWizardLogic } from './logic';
import ProfileWizardHeader from './ProfileWizardHeader';
import { useAppToast } from '@/hooks/useAppToast';
import PhoneInput from '@/components/PhoneInput';
import PhoneVerificationModal from '@/modules/idv/PhoneVerificationModal';
import { ClaimsLogic } from '@/modules/idv';
import { PhoneLogic } from '@/modules/phone';
import { MePrFoService } from '@/services/meprfo';
import { PHONE_LOOKUP_DEBOUNCE_MS, pxToRem } from '@/modules/config';
import { apiCall } from '@/util/api';
import { AuthLogic } from '@/modules/iam';

const PhoneStep: React.FC = () => {
  const {
    userId: contextUserId,
    phone: contextPhone,
    claims,
    setPhone: setContextPhone,
    setPendingPhone: setContextPendingPhone,
    refreshUserContext,
    hasUserId,
  } = useUserContext();
  const {
    phone,
    setPhone,
    originalPhone,
    setOriginalPhone,
    showPhoneModal,
    setShowPhoneModal,
    urlProvidedOrigin,
    routerProvidedOrigin,
    navigateWithOrigin,
    navigateToStep,
    progress,
  } = useProfileWizardContext();
  const [presentToast] = useAppToast();

  useIonViewWillLeave(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  });

  // Tracks a just-completed verification so the modal's trailing dismiss event
  // (which reports 'cancelled') doesn't revert the number we just verified.
  const verificationSucceededRef = useRef(false);

  // Explicit opt-in: the number in the box replaces the number on a preexisting
  // profile (a "new SIM"). Migration re-points the whole profile, so it must be
  // deliberate — inferring it from an edited number silently forked profiles
  // (#441). The checkbox is only OFFERED once the lookup finds no existing
  // profile for the entered number (see noProfileForNumber below); checking it
  // makes the CTA hand off to the dedicated migration flow rather than starting
  // a fresh profile here.
  const [isUpdatedNumber, setIsUpdatedNumber] = useState(false);

  // Determine variant based on context (similar to Step1CurrentProfile pattern)
  const variant = useMemo<
    'no-phone' | 'unverified-phone' | 'verified-phone'
  >(() => {
    if (!contextPhone) return 'no-phone';
    if (ClaimsLogic.isPhoneVerified(claims)) return 'verified-phone';
    return 'unverified-phone';
  }, [contextPhone, claims]);

  // Check if phone number has been changed from original
  const isPhoneChanged = phone !== originalPhone;

  // Once the number is valid, look up — debounced — whether that OPRF already
  // has a profile. It drives the CTA (recover an existing profile → "Next", or
  // start a brand-new one → "Verify new profile number" + a note) AND gates the
  // migration checkbox: switching a preexisting profile onto this number is only
  // offered when the number has no profile of its own. This is display-only;
  // handleNext still does its own authoritative lookup at click time.
  //
  // The result is keyed to the exact normalized number it was fetched for, so
  // we can tell whether it reflects the number CURRENTLY in the box (editing to
  // a new number instantly makes the prior result stale → back to "looking
  // up"). On a failed lookup we record 'error' and fall back to the neutral
  // label rather than blocking the user.
  const [phoneLookup, setPhoneLookup] = useState<{
    value: string;
    result: 'match' | 'no-match' | 'error';
  } | null>(null);

  useEffect(() => {
    const phoneObj = PhoneLogic.tryCreate(phone);
    if (!phoneObj) return;
    const value = phoneObj.value;
    let cancelled = false;
    const handle = setTimeout(() => {
      void MePrFoService.checkPhoneExists(value)
        .then(({ userId }) => {
          if (!cancelled) {
            setPhoneLookup({
              value,
              result: userId ? 'match' : 'no-match',
            });
          }
        })
        .catch(() => {
          if (!cancelled) setPhoneLookup({ value, result: 'error' });
        });
    }, PHONE_LOOKUP_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [phone]);

  const currentPhoneObj = PhoneLogic.tryCreate(phone);
  const phoneIsValid = currentPhoneObj !== null;

  // Has the debounced lookup resolved for the number currently in the box?
  const lookupSettled =
    phoneLookup !== null &&
    currentPhoneObj !== null &&
    phoneLookup.value === currentPhoneObj.value;

  // The lookup resolved for the current number and found NO existing profile.
  // This is the only state where switching a preexisting profile onto this
  // number makes sense, so it's what reveals the migration checkbox.
  const noProfileForNumber =
    lookupSettled && phoneLookup?.result === 'no-match';

  // Migrating requires BOTH the checkbox and a current no-match, so editing to a
  // number that already has a profile silently drops migration mode. When true,
  // tapping the CTA hands off to the migration proof flow.
  const isMigrating = isUpdatedNumber && noProfileForNumber;

  // A valid number whose lookup hasn't resolved yet: hold the CTA disabled and
  // show "Encrypting & looking up…" (the number is obliviously hashed before the
  // lookup) so its label is never wrong and the user can't act before we know
  // whether this is a new profile.
  const isLookingUpPhone = phoneIsValid && !lookupSettled;

  // No existing profile and the user has NOT opted into migration: verifying
  // will START a new profile. Surface it so an accidental number isn't silently
  // forked.
  const willCreateNewProfile = noProfileForNumber && !isMigrating;

  // Drop a stale migration opt-in whenever the number stops being a fresh
  // no-match (edited, matched, or still resolving), so a leftover check never
  // rides along onto a different number.
  useEffect(() => {
    if (!noProfileForNumber && isUpdatedNumber) {
      setIsUpdatedNumber(false);
    }
  }, [noProfileForNumber, isUpdatedNumber]);

  const getDaysUntilExpiration = (date: Date): number => {
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    return diffMs / (1000 * 60 * 60 * 24);
  };

  const determineEmailStep = (): string => {
    return '/profile/steps/email';
  };

  const handleNext = async () => {
    // Migration opt-in: the whole flow (prove control of the old account, then
    // verify + move onto the new number) lives on dedicated steps. Hand off to
    // the proof-method chooser; nothing is staged or verified here.
    if (isMigrating) {
      navigateWithOrigin('/profile/steps/phone-migration-proof', 'forward');
      return;
    }

    const phoneObj = PhoneLogic.create(phone);

    // Sync phone to UserContext immediately so it's available throughout the app
    // (and to the verification modal) before verification completes — but as a
    // pending value that is NOT written through to storage. That keeps the
    // previously stored number intact, so reloading the page while the
    // verification modal is open restores the old number instead of this
    // unverified edit. It's committed to storage only once verified.
    setContextPendingPhone(phoneObj);

    // If on verified phone variant and number unchanged, skip verification.
    if (variant === 'verified-phone' && phone === originalPhone) {
      navigateWithOrigin(determineEmailStep(), 'forward');
      return;
    }

    try {
      // First-time sign-up / returning guest, or an identified user re-verifying
      // their unchanged number.

      // Check if phone exists. The lookup computes the number's OPRF + tail, so
      // we keep them to reuse on the create-new-profile branch below.
      const {
        userId: existingUserId,
        phoneOprf: lookedUpOprf,
        phoneTail: lookedUpTail,
      } = await MePrFoService.checkPhoneExists(phoneObj.value);

      let phoneTail: string;

      if (existingUserId) {
        // Skip re-verification when this number resolves to the current account
        // and is still verified (a plain returning entry).
        if (contextUserId && contextUserId === existingUserId) {
          const phoneExp = ClaimsLogic.phoneVerifExpiration(claims);
          if (phoneExp) {
            const daysUntilExpiration = getDaysUntilExpiration(phoneExp.date);

            if (daysUntilExpiration < 1) {
              // Expiration < 1 day: stay on phone step (verified but expiring soon)
              // Component will re-render with variant 'verified-phone' after refreshUserContext
              setContextPendingPhone(phoneObj);
              await refreshUserContext();
              return;
            } else {
              // Expiration >= 1 day: jump to email step (phone is good, move on)
              setContextPendingPhone(phoneObj);
              const emailStep = determineEmailStep();
              navigateWithOrigin(emailStep, 'forward');
              return;
            }
          }
        }

        if (hasUserId && contextUserId === existingUserId) {
          phoneTail = PhoneLogic.tail(phoneObj);
        } else {
          // Exchange session for the profile that owns this number. This also
          // covers correcting a mistyped number: the throwaway account created
          // for the wrong number leaves a stale session whose phone_tail no
          // longer matches, so we must re-identify before verifying.
          const identifyResponse = await apiCall('/api/auth/identify', {
            method: 'POST',
            body: JSON.stringify({ id: existingUserId }),
          });

          if (!identifyResponse.ok) {
            throw new Error(
              'Failed to exchange session for profile with preexisting phone number'
            );
          }

          await refreshUserContext();

          const authService = AuthLogic.getAuthService();
          const user = await authService.getCurrentUser();

          if (!user) {
            throw new Error(
              'Failed to retrieve exchanged user. You might need to reset your profile.'
            );
          }

          phoneTail = user.phoneTail;
        }
      } else {
        // Create new user, reusing the OPRF + tail computed by the lookup.
        phoneTail = lookedUpTail;

        const createBody: {
          phoneTail: string;
          phoneOprf: string;
        } = {
          phoneTail: phoneTail,
          phoneOprf: lookedUpOprf,
        };

        const response = await apiCall(`/api/users`, {
          method: 'PUT',
          body: JSON.stringify(createBody),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || 'User creation failed');
        }

        const userData = await response.json();
        const newUserId = userData.id;

        const identifyResponse = await apiCall('/api/auth/identify', {
          method: 'POST',
          body: JSON.stringify({ id: newUserId }),
        });

        if (!identifyResponse.ok) {
          const errorText = await identifyResponse.text();
          throw new Error(errorText || 'Failed to authenticate as new user');
        }

        await refreshUserContext();
      }

      // The number stays pending until verification succeeds, so an
      // abandoned change (including a page reload while the modal is open) never
      // overwrites the stored number.
      setContextPendingPhone(phoneObj);

      // Open phone verification modal
      setShowPhoneModal(true);
    } catch (error) {
      presentToast({
        message: error instanceof Error ? error.message : 'An error occurred',
        color: 'danger',
        duration: 3000,
      });
    }
  };

  const handleRefreshVerification = () => {
    setShowPhoneModal(true);
  };

  const handlePhoneVerificationClose = (reason?: string) => {
    setShowPhoneModal(false);

    // A successful verification commits the new number. Closing the modal on
    // success — and the dismiss event that fires right after — must not revert.
    if (reason === 'success') {
      verificationSucceededRef.current = true;
      return;
    }
    if (verificationSucceededRef.current) {
      verificationSucceededRef.current = false;
      return;
    }

    // Cancelled or timed out before verifying. Keep the entered number (a
    // first-time number or an unchanged re-verify — migration never opens this
    // modal), committing through setContextPhone, which clears the pending
    // stash so a later reload won't promote the abandoned number.
    if (reason === 'cancelled' || reason === 'expired') {
      setContextPhone(PhoneLogic.tryCreate(phone) ?? undefined);
    }

    if (reason === 'expired') {
      presentToast({
        message: 'Verification code expired. Please try verifying again.',
        color: 'warning',
        duration: 5000,
      });
    }
  };

  const handlePhoneVerificationSuccess = () => {
    verificationSucceededRef.current = true;

    // The number is verified now — commit it to durable storage (it was set
    // provisionally during handleNext) so a later reload keeps the new number.
    const verified = PhoneLogic.tryCreate(phone);
    if (verified) {
      setContextPhone(verified);
    }
    // The verified number IS the account's number now — move the wizard's
    // change-detection snapshot with it.
    setOriginalPhone(phone);
    setShowPhoneModal(false);
    const emailStep = determineEmailStep();
    navigateWithOrigin(emailStep, 'forward');
  };

  const getPhoneVerificationContinuePath = (): string => {
    // After phone verification, user should advance to email step.
    const emailStep = determineEmailStep();
    const origin = routerProvidedOrigin || urlProvidedOrigin || '/';
    return `${emailStep}?origin=${encodeURIComponent(origin)}`;
  };

  const phoneExp = ClaimsLogic.phoneVerifExpiration(claims);
  const expirationText = phoneExp ? phoneExp.formattedDate : null;

  return (
    <IonPage>
      <ProfileWizardHeader
        title={ProfileWizardLogic.titleForStep('phone')}
        progress={progress}
        onBack={() =>
          navigateToStep(ProfileWizardLogic.previousStep('phone'), 'back')
        }
      />
      <IonContent>
        <div style={{ padding: '20px 20px 120px' }}>
          <IonText>
            {variant === 'no-phone' && (
              <>
                <p style={{ fontSize: pxToRem(16), marginBottom: '10px' }}>
                  Link a phone number for stats tracking, easier resharing and
                  more. Used Snowball before? Enter the number from last time or
                  your new one if it's changed.
                </p>
                <p
                  style={{
                    fontSize: pxToRem(14),
                    color: '#666',
                    marginBottom: '20px',
                  }}
                >
                  NOTE: Snowball encrypts your phone number before storing it in
                  your profile on the server.
                </p>
              </>
            )}
            {variant === 'unverified-phone' && (
              <p style={{ fontSize: pxToRem(16), marginBottom: '20px' }}>
                Here's the phone number we have for you. Press Next to verify.
                Changed numbers? Type the new one here.
              </p>
            )}
            {variant === 'verified-phone' && (
              <p style={{ fontSize: pxToRem(16), marginBottom: '20px' }}>
                This is the verified phone number we have for you.
                {expirationText &&
                  ` Verification will expire in ${expirationText}.`}{' '}
                Changed numbers? Type the new one here.
              </p>
            )}
          </IonText>
          <IonItem>
            <IonLabel position="stacked">
              <IonIcon icon={callOutline} style={{ marginRight: '5px' }} />
              Phone number
            </IonLabel>
            <PhoneInput
              value={phone}
              onPhoneChange={setPhone}
              placeholder="(555) 123-4567"
              showValidation={true}
            />
          </IonItem>
          {/* No profile for this number: explain that verifying starts a
                  new one, directly below the field, with the migration opt-in
                  checkbox just beneath. Checking the box keeps this note and
                  changes only the CTA to "Link profile to new number". */}
          {noProfileForNumber && (
            <>
              <IonText>
                <p
                  style={{
                    fontSize: pxToRem(13),
                    color: 'var(--ion-color-medium)',
                    margin: '10px 0 0',
                  }}
                  data-testid="phone-no-match-note"
                >
                  We didn't find a Snowball profile for this number, so
                  verifying will start a new one. Double-check it's the number
                  you use with Snowball.
                </p>
              </IonText>
              <IonItem lines="none">
                <IonCheckbox
                  checked={isUpdatedNumber}
                  onIonChange={e => setIsUpdatedNumber(e.detail.checked)}
                  labelPlacement="end"
                  data-testid="phone-updated-number-checkbox"
                >
                  <span style={{ fontSize: pxToRem(14), whiteSpace: 'normal' }}>
                    This is an updated number for a preexisting profile
                  </span>
                </IonCheckbox>
              </IonItem>
            </>
          )}
        </div>
      </IonContent>

      <FooterDock background="transparent">
        {variant === 'verified-phone' && !isPhoneChanged && !isMigrating && (
          <FooterDockRow>
            <SmartCaseButton
              expand="block"
              onClick={handleRefreshVerification}
              color="secondary"
              style={{ margin: 0 }}
            >
              Refresh verification
            </SmartCaseButton>
          </FooterDockRow>
        )}
        <FooterDockRow landmark>
          <SmartCaseButton
            expand="block"
            onClick={handleNext}
            disabled={!phoneIsValid || isLookingUpPhone}
            color="primary"
            data-testid="profile-wizard-next"
            style={{ margin: 0 }}
          >
            {isLookingUpPhone
              ? 'Encrypting & looking up…'
              : isMigrating
                ? 'Relink profile to new phone'
                : willCreateNewProfile
                  ? 'Verify new profile number'
                  : 'Next'}
          </SmartCaseButton>
        </FooterDockRow>
      </FooterDock>

      <PhoneVerificationModal
        isOpen={showPhoneModal}
        onClose={handlePhoneVerificationClose}
        onSuccess={handlePhoneVerificationSuccess}
        continuePath={getPhoneVerificationContinuePath()}
      />
    </IonPage>
  );
};

export default PhoneStep;
